import { useState, useEffect } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { publicApi } from '../services/api'
import { formatCurrency } from '../config'
import type { Event, RegistrationFormData, Golfer, Contact, RegistrationModeResponse, ReferralSource } from '../types'
import LoadingSpinner from '../components/common/LoadingSpinner'
import ReferralSourceField from '../components/common/ReferralSourceField'
import { formatPhoneNumber, lowercaseEmail, isValidEmail, isValidPhone } from '../utils/formatters'

const GOLF_PRICE_CENTS = 15000

const focusField = (id: string) => {
  const el = document.getElementById(id)
  if (el) {
    el.focus()
    el.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }
}
// Banquet functionality hidden - see docs/BANQUET_RESTORATION.md to restore
// const BANQUET_PRICE_CENTS = 5000

const emptyGolfer = (): Golfer => ({ name: '', email: '', phone: '' })
const emptyContact = (): Contact => ({ name: '', email: '', phone: '' })

export default function Registration() {
  const { slug } = useParams<{ slug: string }>()
  const [searchParams] = useSearchParams()
  const [event, setEvent] = useState<Event | null>(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Registration mode state
  const [registrationMode, setRegistrationMode] = useState<RegistrationModeResponse | null>(null)
  const [waitlistSuccess, setWaitlistSuccess] = useState<{ position: number } | null>(null)

  // Form state
  const [primaryContact, setPrimaryContact] = useState<Contact>(emptyContact())
  const [golfersCount, setGolfersCount] = useState(0)
  // Banquet hidden - keep state but always 0
  const banquetCount = 0
  const [golfers, setGolfers] = useState<Golfer[]>([])
  const [teamName, setTeamName] = useState('')
  const [paidGolferIndices, setPaidGolferIndices] = useState<Set<number>>(new Set())
  const [referralSource, setReferralSource] = useState<ReferralSource | ''>('')
  const [referralSourceOther, setReferralSourceOther] = useState('')

  // Prefill from URL params (for buddy registration links)
  const referringRegistrationId = searchParams.get('ref') || undefined
  useEffect(() => {
    if (searchParams.get('prefill') === 'true') {
      const name = searchParams.get('name') || ''
      const email = searchParams.get('email') || ''
      const phone = searchParams.get('phone') || ''
      const prefillTeamName = searchParams.get('teamName') || ''
      if (name || email) {
        setPrimaryContact({ name, email, phone })
        setGolfersCount(1)
        setGolfers([{ name, email, phone }])
        setPaidGolferIndices(new Set([0]))
      }
      if (prefillTeamName) setTeamName(prefillTeamName)
    }
  }, [searchParams])

  // Load event and registration mode
  useEffect(() => {
    async function fetchData() {
      if (!slug) return
      try {
        const [eventData, modeData] = await Promise.all([
          publicApi.getEventBySlug(slug),
          publicApi.getRegistrationState(slug)
        ])
        setEvent(eventData)
        setRegistrationMode(modeData)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Event not found')
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [slug])

  // Update golfers array when count changes
  useEffect(() => {
    const newGolfers = [...golfers]
    while (newGolfers.length < golfersCount) {
      newGolfers.push(emptyGolfer())
    }
    while (newGolfers.length > golfersCount) {
      newGolfers.pop()
    }
    setGolfers(newGolfers)
    // Manage paid golfer indices: new golfers default to paid, trim removed ones
    setPaidGolferIndices(prev => {
      const updated = new Set(prev)
      // Add any new indices as paid by default
      for (let i = 0; i < golfersCount; i++) {
        if (!prev.has(i) && i >= golfers.length) {
          updated.add(i)
        }
      }
      // Remove indices beyond the new count
      for (const idx of updated) {
        if (idx >= golfersCount) updated.delete(idx)
      }
      // First time (all indices missing), mark all as paid
      if (updated.size === 0 && golfersCount > 0) {
        for (let i = 0; i < golfersCount; i++) updated.add(i)
      }
      return updated
    })
  }, [golfersCount])

  const updateGolfer = (index: number, field: keyof Golfer, value: string) => {
    const newGolfers = [...golfers]
    newGolfers[index] = { ...newGolfers[index], [field]: value }
    setGolfers(newGolfers)
  }

  const toggleGolferPaid = (index: number) => {
    setPaidGolferIndices(prev => {
      const updated = new Set(prev)
      if (updated.has(index)) {
        updated.delete(index)
      } else {
        updated.add(index)
      }
      return updated
    })
  }

  const copyPrimaryToFirstGolfer = () => {
    if (golfers.length > 0) {
      const newGolfers = [...golfers]
      newGolfers[0] = { ...primaryContact }
      setGolfers(newGolfers)
    }
  }

  const paidGolfersCount = paidGolferIndices.size
  const totalAmount = paidGolfersCount * GOLF_PRICE_CENTS

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (golfersCount === 0) {
      setError('Please select at least one golfer')
      return
    }

    if (!isWaitlistMode && paidGolfersCount === 0) {
      setError('At least one golfer must be marked as paid to proceed to payment')
      return
    }

    // Validate primary contact fields
    if (!primaryContact.name.trim()) {
      setError('Please enter the primary contact name')
      focusField('primary-name')
      return
    }
    if (!primaryContact.email.trim()) {
      setError('Please enter the primary contact email')
      focusField('primary-email')
      return
    }
    if (!isValidEmail(primaryContact.email)) {
      setError('Please enter a valid email address for the primary contact')
      focusField('primary-email')
      return
    }
    if (!primaryContact.phone.trim()) {
      setError('Please enter the primary contact phone number')
      focusField('primary-phone')
      return
    }
    if (!isValidPhone(primaryContact.phone)) {
      setError('Please enter a valid 10-digit phone number for the primary contact')
      focusField('primary-phone')
      return
    }

    // Validate golfer emails and phones
    for (let i = 0; i < golfersCount; i++) {
      if (!golfers[i].name.trim()) {
        setError(`Please enter a name for Golfer ${i + 1}`)
        focusField(`golfer-${i}-name`)
        return
      }
      if (!golfers[i].email.trim()) {
        setError(`Please enter an email for Golfer ${i + 1}`)
        focusField(`golfer-${i}-email`)
        return
      }
      if (!isValidEmail(golfers[i].email)) {
        setError(`Golfer ${i + 1} has an invalid email address`)
        focusField(`golfer-${i}-email`)
        return
      }
      if (!golfers[i].phone.trim()) {
        setError(`Please enter a phone number for Golfer ${i + 1}`)
        focusField(`golfer-${i}-phone`)
        return
      }
      if (!isValidPhone(golfers[i].phone)) {
        setError(`Golfer ${i + 1} has an invalid phone number (10 digits required)`)
        focusField(`golfer-${i}-phone`)
        return
      }
    }

    // Validate referral source
    if (!referralSource) {
      setError('Please select how you heard about us')
      return
    }
    if (referralSource === 'Other' && !referralSourceOther.trim()) {
      setError('Please specify how you heard about us')
      return
    }

    setSubmitting(true)

    try {
      const formData: RegistrationFormData = {
        eventSlug: slug!,
        primaryContact,
        golfersCount,
        banquetTicketsCount: banquetCount,
        golfers: golfers.slice(0, golfersCount).map((g, i) => ({
          ...g,
          paying: paidGolferIndices.has(i),
        })),
        teamName: teamName || undefined,
        paidGolferIndices: Array.from(paidGolferIndices),
        referringRegistrationId,
        referralSource: referralSource as ReferralSource,
        referralSourceOther: referralSource === 'Other' ? referralSourceOther.trim() : undefined,
      }

      // Check if we're in waitlist mode
      if (registrationMode?.mode === 'waitlist') {
        const response = await publicApi.joinWaitlist(formData)
        setWaitlistSuccess({ position: response.waitlistPosition })
        setSubmitting(false)
      } else {
        // Normal registration with payment
        const response = await publicApi.createCheckoutSession(formData)
        // Redirect to Stripe Checkout
        window.location.href = response.checkoutUrl
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit registration')
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="py-12">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error && !event) {
    return (
      <div className="card text-center py-12">
        <p className="text-red-600 mb-4">{error}</p>
        <a href="/" className="btn btn-primary">
          Back to Events
        </a>
      </div>
    )
  }

  if (!event) {
    return null
  }

  // Show waitlist success message
  if (waitlistSuccess) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="card text-center">
          <div className="mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">You're on the Waitlist!</h1>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6 text-left">
            <h2 className="font-semibold text-gray-800 mb-3">What happens next?</h2>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start gap-2">
                <span className="text-sla-blue mt-1">✓</span>
                <span>You'll receive a confirmation email shortly at <strong>{primaryContact.email}</strong></span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-sla-blue mt-1">✓</span>
                <span>Registration is currently at capacity</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-sla-blue mt-1">✓</span>
                <span>When a spot opens, we'll email you with payment instructions</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-sla-blue mt-1">✓</span>
                <span>You'll have a limited time to complete payment and secure your spot</span>
              </li>
            </ul>
          </div>

          <div className="text-gray-600">
            <p>Thank you for your interest in supporting School Lunch Angels!</p>
            <p className="mt-4 text-sm text-gray-500">You may close this window.</p>
          </div>
        </div>
      </div>
    )
  }

  // Check if event is closed
  if (registrationMode?.mode === 'closed') {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="card text-center">
          <h1 className="text-3xl font-bold text-gray-800 mb-4">{event.name}</h1>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <p className="text-lg text-gray-700">
              Registration is currently closed for this event.
            </p>
            <p className="text-gray-600 mt-2">
              Registration has reached capacity. Please check back later.
            </p>
          </div>
        </div>
      </div>
    )
  }

  const isWaitlistMode = registrationMode?.mode === 'waitlist'

  return (
    <div>
      {/* Event Hero Section */}
      <div className="card mb-8 overflow-hidden">
        <div className="-mx-6 -mt-6 mb-6 overflow-hidden">
          <img
            src="/images/angels-on-the-green.png"
            alt="Angels on the Green Golf Tournament"
            className="w-full h-48 md:h-64 object-cover object-center"
          />
        </div>
        <div className="flex flex-col md:flex-row items-center gap-6">
          <img
            src="/images/logo.png"
            alt="School Lunch Angels"
            className="h-32 md:h-40 w-auto flex-shrink-0"
          />
          <div className="text-center md:text-left">
            <h1 className="text-3xl font-bold text-sla-green mb-2">Angels on the Green</h1>
            <p className="text-xl text-gray-700 mb-4">Best Ball Scramble Golf Tournament</p>
            <div className="text-gray-600 space-y-1">
              <p className="font-medium">April 11, 2026</p>
              <p>11:00 AM Check In | 12:00 PM Shotgun Start | Awards Ceremony to Follow</p>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="font-semibold text-gray-800">Callaway Resort & Gardens</p>
              <p className="text-gray-600">Lake View Golf Course</p>
              <p className="text-gray-600">3396 Golf House Drive, Pine Mountain, GA 31822</p>
            </div>
          </div>
        </div>
      </div>

      {/* Waitlist Mode Banner */}
      {isWaitlistMode && (
        <div className="card bg-yellow-50 border-2 border-yellow-300 mb-8">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Waitlist Registration</h3>
              <p className="text-gray-700 mb-2">
                Registration is currently at capacity. You can join the waitlist below - <strong>no payment required yet</strong>.
              </p>
              <p className="text-gray-700">
                You'll receive an email when spots become available with instructions to complete your registration.
              </p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Primary Contact */}
        <div className="card">
          <h2 className="text-xl font-bold text-gray-800 mb-4">
            Primary Contact Information
          </h2>
          <p className="text-gray-600 text-sm mb-4">
            This person will receive confirmation emails and be the main point
            of contact.
          </p>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="label">Full Name *</label>
              <input
                id="primary-name"
                type="text"
                className="input"
                value={primaryContact.name}
                onChange={(e) =>
                  setPrimaryContact({ ...primaryContact, name: e.target.value })
                }
                required
              />
            </div>
            <div>
              <label className="label">Email *</label>
              <input
                id="primary-email"
                type="email"
                className="input"
                value={primaryContact.email}
                onChange={(e) =>
                  setPrimaryContact({ ...primaryContact, email: e.target.value })
                }
                onBlur={(e) =>
                  setPrimaryContact({ ...primaryContact, email: lowercaseEmail(e.target.value) })
                }
                required
              />
            </div>
            <div className="md:col-span-2">
              <label className="label">Phone *</label>
              <input
                id="primary-phone"
                type="tel"
                className="input md:w-1/2"
                value={primaryContact.phone}
                onChange={(e) =>
                  setPrimaryContact({ ...primaryContact, phone: formatPhoneNumber(e.target.value) })
                }
                required
              />
            </div>
          </div>
        </div>

        {/* Registration Options */}
        <div className="card">
          <h2 className="text-xl font-bold text-gray-800 mb-4">
            Registration Options
          </h2>

          <div className="space-y-6">
            {/* Golf Registration */}
            <div className="border rounded-lg p-4">
              <div className="flex justify-between items-center mb-2">
                <div>
                  <h3 className="font-semibold text-gray-800">
                    Golf Registration
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Includes golf, cart, and lunch
                  </p>
                </div>
                <span className="text-lg font-bold text-sla-blue">
                  {formatCurrency(GOLF_PRICE_CENTS)} each
                </span>
              </div>

              <div className="flex items-center gap-4">
                <label className="label mb-0">Number of Golfers:</label>
                <select
                  className="input w-24"
                  value={golfersCount}
                  onChange={(e) => setGolfersCount(Number(e.target.value))}
                >
                  {(isWaitlistMode ? [0, 1] : [0, 1, 2, 3, 4, 5, 6, 7, 8]).map(n => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Banquet Only - HIDDEN - see docs/BANQUET_RESTORATION.md */}
          </div>
        </div>

        {/* Golfer Details */}
        {golfersCount > 0 && (
          <div className="card">
            <h2 className="text-xl font-bold text-gray-800 mb-4">
              Golfer Information
            </h2>
            <p className="text-gray-600 text-sm mb-4">
              Please provide details for each golfer.
            </p>

            {golfers.map((golfer, index) => {
              const isPaid = paidGolferIndices.has(index)
              return (
                <div
                  key={index}
                  className={`border rounded-lg p-4 mb-4 last:mb-0 ${!isPaid ? 'bg-gray-50 border-dashed' : ''}`}
                >
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-semibold text-gray-700">
                      Golfer {index + 1}
                    </h3>
                    <div className="flex items-center gap-4">
                      {index === 0 && (
                        <button
                          type="button"
                          onClick={copyPrimaryToFirstGolfer}
                          className="text-sm text-sla-blue hover:underline"
                        >
                          Copy from primary contact
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isPaid}
                        onChange={() => toggleGolferPaid(index)}
                        className="w-4 h-4"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        I am paying for this golfer
                      </span>
                    </label>
                    {!isPaid && (
                      <p className="text-xs text-orange-600 mt-1 ml-6">
                        This golfer will receive an email to register and pay for themselves.
                      </p>
                    )}
                  </div>

                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <label className="label">Full Name *</label>
                      <input
                        id={`golfer-${index}-name`}
                        type="text"
                        className="input"
                        value={golfer.name}
                        onChange={(e) =>
                          updateGolfer(index, 'name', e.target.value)
                        }
                        required
                      />
                    </div>
                    <div>
                      <label className="label">Email *</label>
                      <input
                        id={`golfer-${index}-email`}
                        type="email"
                        className="input"
                        value={golfer.email}
                        onChange={(e) =>
                          updateGolfer(index, 'email', e.target.value)
                        }
                        onBlur={(e) =>
                          updateGolfer(index, 'email', lowercaseEmail(e.target.value))
                        }
                        required
                      />
                    </div>
                    <div>
                      <label className="label">Phone *</label>
                      <input
                        id={`golfer-${index}-phone`}
                        type="tel"
                        className="input"
                        value={golfer.phone}
                        onChange={(e) =>
                          updateGolfer(index, 'phone', formatPhoneNumber(e.target.value))
                        }
                        required
                      />
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {/* Team Preferences */}
        {golfersCount > 0 && (
          <div className="card">
            <h2 className="text-xl font-bold text-gray-800 mb-4">
              Team Preferences (Optional)
            </h2>

            <div className="space-y-4">
              <div>
                <label className="label">Team Name</label>
                <input
                  type="text"
                  className="input"
                  placeholder="e.g., The Birdies"
                  value={teamName}
                  onChange={(e) => setTeamName(e.target.value)}
                />
              </div>
            </div>
          </div>
        )}

        {/* Order Summary */}
        <div className="card bg-gray-50">
          <h2 className="text-xl font-bold text-gray-800 mb-4">
            {isWaitlistMode ? 'Registration Summary' : 'Order Summary'}
          </h2>

          <div className="space-y-2 mb-4">
            {golfersCount > 0 && (
              <>
                <div className="flex justify-between">
                  <span>Golf Registration x {paidGolfersCount}{paidGolfersCount < golfersCount ? ` (paying for ${paidGolfersCount} of ${golfersCount} golfers)` : ''}</span>
                  {!isWaitlistMode && <span>{formatCurrency(paidGolfersCount * GOLF_PRICE_CENTS)}</span>}
                </div>
                {paidGolfersCount < golfersCount && (
                  <p className="text-xs text-orange-600">
                    {golfersCount - paidGolfersCount} golfer{golfersCount - paidGolfersCount > 1 ? 's' : ''} will receive an email to register and pay individually
                  </p>
                )}
              </>
            )}
          </div>

          {isWaitlistMode ? (
            <div className="border-t pt-4">
              <p className="text-gray-700 font-semibold">No payment required to join waitlist</p>
              <p className="text-sm text-gray-600 mt-1">
                Payment will be collected when spots become available
              </p>
            </div>
          ) : (
            <div className="border-t pt-4">
              <div className="flex justify-between text-xl font-bold">
                <span>Total</span>
                <span className="text-sla-blue">{formatCurrency(totalAmount)}</span>
              </div>
            </div>
          )}
        </div>

        {/* Referral Source */}
        {golfersCount > 0 && (
          <ReferralSourceField
            value={referralSource}
            otherValue={referralSourceOther}
            onChange={setReferralSource}
            onOtherChange={setReferralSourceOther}
            error={!!error && !referralSource}
          />
        )}

        {/* Refund Policy Notice */}
        {!isWaitlistMode && golfersCount > 0 && (
          <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4 text-sm text-yellow-800">
            <strong>Refund Policy:</strong> Registrations are non-refundable after March 1st but are transferable. A $5 fee per golfer registration applies to any refund processed before that date.
          </div>
        )}

        {/* Rain-or-Shine Notice */}
        <p className="text-xs text-gray-500 text-center">
          This is a rain-or-shine event. In case of extreme circumstances (natural disasters, government orders, etc.), the backup date is Saturday, May 9, 2026.
        </p>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* Submit Button */}
        <div className="text-center">
          <button
            type="submit"
            disabled={submitting || golfersCount === 0}
            className="btn btn-primary text-lg px-8 py-3"
          >
            {submitting ? (
              <span className="flex items-center gap-2">
                <LoadingSpinner size="sm" />
                {isWaitlistMode ? 'Joining Waitlist...' : 'Processing...'}
              </span>
            ) : isWaitlistMode ? (
              'Join Waitlist (No Payment Required)'
            ) : (
              `Proceed to Payment - ${formatCurrency(totalAmount)}`
            )}
          </button>
          <p className="text-gray-500 text-sm mt-2">
            {isWaitlistMode
              ? "You'll receive a confirmation email with next steps"
              : "You'll be redirected to Stripe for secure payment"
            }
          </p>
        </div>
      </form>
    </div>
  )
}
